import GithubSlugger from 'github-slugger'

// Headings for the on-page table of contents, and for the link-coverage test.
//
// Ids are produced with github-slugger — the same library `rehype-slug` uses
// internally, constructed fresh per document so its duplicate-suffix counter
// (`#units`, `#units-1`) matches what the renderer emits. Reusing one instance
// across documents would silently drift the second page's ids.

export interface Heading {
  depth: number
  text: string
  id: string
}

// Fenced blocks first: these docs are full of shell and GraphQL samples where a
// `# comment` line would otherwise be read as a heading.
const withoutFences = (markdown: string): string =>
  markdown.replace(/^```[\s\S]*?^```/gm, '')

const HEADING = /^(#{1,6})\s+(.+?)\s*$/gm

// Heading text is markdown itself — `### \`fee\` handling` is common here — and
// the slugger runs on the rendered text, not the source.
const asPlainText = (markdown: string): string =>
  markdown
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/\*([^*]*)\*/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]+>/g, '')
    .trim()

export const headingsOf = (markdown: string): Heading[] => {
  const slugger = new GithubSlugger()
  const source = withoutFences(markdown)
  const headings: Heading[] = []

  for (const match of source.matchAll(HEADING)) {
    const text = asPlainText(match[2])
    if (!text) continue
    headings.push({ depth: match[1].length, text, id: slugger.slug(text) })
  }

  return headings
}

/** Every heading id on a page, for verifying that `#anchor` links resolve. */
export const anchorsOf = (markdown: string): Set<string> =>
  new Set(headingsOf(markdown).map(heading => heading.id))
